/**
 * Prompt Library Utilities
 *
 * Uses Vite glob import to auto-discover all .md files in src/prompt-lib/
 * No manual index.json needed - just add/remove .md files and they're included on next build.
 */

// Vite glob import - auto-discovers all .md files at build time
const promptModules = import.meta.glob('/src/prompt-lib/**/*.md', {
  query: '?raw',
  eager: true,
});

export type StackType = 'frontend' | 'fullstack';

export interface PromptFrontmatter {
  title: string;
  description: string;
  category: string;
  stackType: StackType;
}

export interface Prompt {
  id: string;
  path: string;
  title: string;
  description: string;
  category: string;
  stackType: StackType;
  content: string;
}

export interface CategoryConfig {
  id: string;
  label: string;
  order: number;
}

// Category display configuration
export const CATEGORIES: CategoryConfig[] = [
  { id: 'website-apps', label: 'Website & Apps', order: 1 },
  { id: 'dashboard-feeds', label: 'Dashboard & Feeds', order: 2 },
  { id: 'games', label: 'Games', order: 3 },
];

/**
 * Parse YAML frontmatter from markdown content
 */
export function parseFrontmatter(content: string): {
  frontmatter: PromptFrontmatter;
  body: string;
} {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return {
      frontmatter: { title: 'Untitled', description: '', category: 'other', stackType: 'frontend' },
      body: content,
    };
  }

  const [, frontmatterStr, body] = match;
  const frontmatter: PromptFrontmatter = {
    title: 'Untitled',
    description: '',
    category: 'other',
    stackType: 'frontend',
  };

  // Simple YAML parsing for our use case
  const lines = frontmatterStr.split('\n');
  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();

    if (key === 'title') frontmatter.title = value;
    if (key === 'description') frontmatter.description = value;
    if (key === 'category') frontmatter.category = value;
    if (key === 'stackType' && (value === 'frontend' || value === 'fullstack')) {
      frontmatter.stackType = value;
    }
  }

  return { frontmatter, body };
}

/**
 * Extract final prompt from markdown content
 * Looks for content between <!-- FINAL_PROMPT_START --> and <!-- FINAL_PROMPT_END -->
 */
export function extractFinalPrompt(content: string): string {
  const startMarker = '<!-- FINAL_PROMPT_START -->';
  const endMarker = '<!-- FINAL_PROMPT_END -->';

  const startIndex = content.indexOf(startMarker);
  const endIndex = content.indexOf(endMarker);

  if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
    return '';
  }

  return content.slice(startIndex + startMarker.length, endIndex).trim();
}

/**
 * Extract guide content (everything before FINAL_PROMPT_START marker)
 */
export function extractGuideContent(content: string): string {
  const startMarker = '<!-- FINAL_PROMPT_START -->';
  const startIndex = content.indexOf(startMarker);

  if (startIndex === -1) {
    return content;
  }

  return content.slice(0, startIndex).trim();
}

/**
 * Get all prompts from the prompt-lib folder
 */
export function getAllPrompts(): Prompt[] {
  return Object.entries(promptModules).map(([path, module]) => {
    const content = (module as { default: string }).default;
    const { frontmatter, body } = parseFrontmatter(content);

    // Extract id from path: /src/prompt-lib/website-apps/product-showcase-kiosk.md -> product-showcase-kiosk
    const fileName = path.split('/').pop() || '';
    const id = fileName.replace('.md', '');

    return {
      id,
      path,
      title: frontmatter.title,
      description: frontmatter.description,
      category: frontmatter.category,
      stackType: frontmatter.stackType,
      content: body,
    };
  });
}

/**
 * Get prompts grouped by category
 */
export function getPromptsByCategory(): Map<string, Prompt[]> {
  const prompts = getAllPrompts();
  const grouped = new Map<string, Prompt[]>();

  // Initialize all categories
  for (const cat of CATEGORIES) {
    grouped.set(cat.id, []);
  }

  // Group prompts
  for (const prompt of prompts) {
    const existing = grouped.get(prompt.category) || [];
    existing.push(prompt);
    grouped.set(prompt.category, existing);
  }

  return grouped;
}

/**
 * Get a single prompt by ID
 */
export function getPromptById(id: string): Prompt | undefined {
  const prompts = getAllPrompts();
  return prompts.find((p) => p.id === id);
}

/**
 * Search prompts by query (searches title and description)
 */
export function searchPrompts(query: string): Prompt[] {
  if (!query.trim()) {
    return getAllPrompts();
  }

  const lowerQuery = query.toLowerCase();
  return getAllPrompts().filter(
    (prompt) =>
      prompt.title.toLowerCase().includes(lowerQuery) ||
      prompt.description.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Filter prompts by category
 */
export function filterPromptsByCategory(
  category: string | null
): Map<string, Prompt[]> {
  if (!category || category === 'all') {
    return getPromptsByCategory();
  }

  const prompts = getAllPrompts().filter((p) => p.category === category);
  const grouped = new Map<string, Prompt[]>();
  grouped.set(category, prompts);

  return grouped;
}
